import React, { useRef, useEffect } from 'lib/react';

import { EVGrid, EVTypography, EVButton } from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';

import TextInputField from 'commonComponents/textInputField';
import SRSBranchEmployeeLabelForm from './SRSBranchEmployeeLabelComponent';
import SRSInfoBanner from '../SRSInfoBanner/SRSInfoBanner.component';
import SRSBranchEmployeeFormStyles from './SRSBranchEmployeeFormStyles';

export default function SRSBranchEmployeeForm({
  isLoading,
  srsOrgNodeName,
  srsBranchEmployeeInfo,
  srsBranchEmployeeInfoErrors,
  handleChange,
  handleErrorChange,
  handleSubmit,
  updateElementRefs,
  srsInfoVerified,
}) {
  const classes = SRSBranchEmployeeFormStyles();
  const { t } = useTranslation();

  const elementRefInfo = ['srsCustId', 'srsShipNumber'].reduce((refInfo, id) => ({ ...refInfo, [id]: { id, elementRef: useRef(null), message: '' } }), {});

  useEffect(() => {
    if (updateElementRefs === null) return;
    updateElementRefs(elementRefInfo);
  }, []);

  const validateFieldAndHandleChange = (
    fieldId,
    { target: { id, value } },
    validator,
    requiredFieldMessage,
    invalidFieldMessage,
  ) => {
    const targetId = fieldId || id;
    handleChange(targetId, value);
    if (value === '') {
      handleErrorChange(targetId, t('common__field_required'), requiredFieldMessage);
    } else if (!validator()) {
      handleErrorChange(targetId, t('common__field_invalid'), invalidFieldMessage);
    } else {
      handleErrorChange(targetId, '', '');
    }
  };
  return (
    <EVGrid className={classes.container}>
      <EVGrid className={classes.headerContainer}>
        <SRSInfoBanner srsOrgNodeName={srsOrgNodeName} />
        { !srsInfoVerified && <EVTypography className={classes.header}>{t('order__srs_branch_employee__header')}</EVTypography>}
      </EVGrid>
      {srsInfoVerified && !isLoading && SRSBranchEmployeeLabelForm
        ? (<SRSBranchEmployeeLabelForm srsBranchEmployeeInfo={srsBranchEmployeeInfo} />)
        : (
          <form className={classes.formContainer} onSubmit={handleSubmit}>
            <EVGrid className={classes.inputContainer}>
              <EVGrid className={classes.fieldContainer}>
                <TextInputField
                  inputId="srsCustId"
                  inputValue={srsBranchEmployeeInfo.srsCustId}
                  handleChange={(event) => validateFieldAndHandleChange(
                    'srsCustId',
                    event,
                    () => /^[a-zA-Z0-9]*$/.test(event.target.value) && event.target.value.length <= 15,
                    t('order__srs_branch_employee__customer_id_required'),
                    t('order__srs_branch_employee__customer_id_invalid'),
                  )}
                  labelText={t('order__srs_branch_employee__customer_id')}
                  inputLabelProps={{
                    className: classes.label,
                    classes: { asterisk: classes.asterisk },
                    required: true,
                    error: srsBranchEmployeeInfoErrors.srsCustId.length > 0,
                  }}
                  inputFieldProps={{
                    fullWidth: true,
                    inputProps: { maxLength: 15 },
                    classes: { root: classes.textInput },
                    FormHelperTextProps: { classes: { error: classes.inputHelperText } },
                    inputRef: elementRefInfo.srsCustId.elementRef,
                  }}
                  helperText={srsBranchEmployeeInfoErrors.srsCustId}
                />
              </EVGrid>
              <EVGrid className={classes.fieldContainer}>
                <TextInputField
                  inputId="srsShipNumber"
                  inputValue={srsBranchEmployeeInfo.srsShipNumber}
                  handleChange={(event) => validateFieldAndHandleChange(
                    'srsShipNumber',
                    event,
                    () => /^[0-9]*$/.test(event.target.value) && event.target.value.length <= 5,
                    t('order__srs_branch_employee__ship_number_required'),
                    t('order__srs_branch_employee__ship_number_invalid'),
                  )}
                  labelText={t('order__srs_branch_employee__ship_number')}
                  inputLabelProps={{
                    className: classes.label,
                    classes: { asterisk: classes.asterisk },
                    required: true,
                    error: srsBranchEmployeeInfoErrors.srsShipNumber.length > 0,
                  }}
                  inputFieldProps={{
                    fullWidth: true,
                    inputProps: { maxLength: 15 },
                    classes: { root: classes.textInput },
                    FormHelperTextProps: { classes: { error: classes.inputHelperText } },
                    inputRef: elementRefInfo.srsShipNumber.elementRef,
                  }}
                  helperText={srsBranchEmployeeInfoErrors.srsShipNumber}
                />
              </EVGrid>
            </EVGrid>
            <EVGrid container wrap="nowrap">
              <EVButton
                type="submit"
                id="submitBtn"
                variant="contained"
                style={{ backgroundColor: '#007CAD', color: '#FFFFFF' }}
                className={classes.button}
                loading={isLoading}
                disabled={isLoading}
              >
                {t('common_submit')}
              </EVButton>
            </EVGrid>
          </form>
        )}
    </EVGrid>
  );
}
SRSBranchEmployeeForm.defaultProps = {
  srsInfoVerified: false,
};

SRSBranchEmployeeForm.propTypes = {
  srsOrgNodeName: PropTypes.string.isRequired,
  srsBranchEmployeeInfo: PropTypes.shape({
    srsCustId: PropTypes.string.isRequired,
    srsShipNumber: PropTypes.string.isRequired,
  }).isRequired,
  srsBranchEmployeeInfoErrors: PropTypes.shape({
    srsCustId: PropTypes.string,
    srsShipNumber: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  updateElementRefs: PropTypes.func.isRequired,
  srsInfoVerified: PropTypes.bool,
};
